import { useTranslation } from 'react-i18next';
import { BedDouble, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { isPriced } from '@/lib/allocation/price';
import type { Reservation, ReservationStatus } from '@/lib/booking/types';
import { Unpriced } from './unpriced';

// A durable reservation row (status badge + cancel saga). Pure presentation —
// takes its data + callbacks via props, no scope coupling. Shared by the
// assignment board and the event allocation surface.

const STATUS_STYLE: Record<ReservationStatus, string> = {
  BOOKED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  HELD: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  PENDING: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  CANCELLED: 'bg-muted text-muted-foreground',
  FAILED: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
  CANCEL_FAILED: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
};

export function ReservationRow({
  reservation: r,
  cancelPending,
  onCancel,
}: {
  reservation: Reservation;
  cancelPending: boolean;
  onCancel: () => void;
}) {
  const { t } = useTranslation('cases');
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-border p-2">
      <div className="min-w-0">
        <p className="flex items-center gap-1.5 font-medium">
          <BedDouble className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          {r.rooms ? `${r.rooms} ${t('common:holds.rooms')}` : t('reservation.fallback')}
        </p>
        <p className="text-xs text-muted-foreground">
          {/* The airline's price (BFF-converted); «No disponible» without a price card. */}
          {isPriced(r.total) ? `${r.currency} ${r.total}` : <Unpriced />}
          {r.status === 'CANCEL_FAILED' ? t('reservation.cancelFailed') : ''}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
            STATUS_STYLE[r.status],
          )}
        >
          {r.status === 'PENDING' ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
          {r.status}
        </span>
        {r.status === 'BOOKED' || r.status === 'CANCEL_FAILED' ? (
          <Button size="sm" variant="outline" disabled={cancelPending} onClick={onCancel}>
            {t('common:common.cancel')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
